import { animate, type AnimationPlaybackControls } from "framer-motion";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** How long a line takes to draw itself in or back out, or to go over to the cards it is now between. */
const TRIAGE_LINK_MOVE_SECONDS = 0.15;
/** How long a line takes to swoosh away as its payment is booked. */
const TRIAGE_LINK_SWOOSH_SECONDS = 0.1;

/**
 * The lines drawn across the page between two cards that are linked: the open
 * payment and the suggestion picked for it, or a payment and what Similar's
 * line-up has beside it, or Similar's payments and the post picked for them all.
 * Each end is a ball on its card (``TriageLinkBall``, ``data-link-from`` /
 * ``data-link-to``, a link's ends sharing its name - several leaving from
 * where one comes to, as Similar's do);
 * a line is drawn between the two while both are engaged, in steps - over
 * both panels and the divider between them, read from where the balls are
 * each frame, so it follows the cards as they scroll and move - and none while
 * either end is under a card held over it. One to or from a card scrolled out
 * of its list - the suggestion picked, a row of Similar's - runs on up or down
 * to that list's edge, off it where the card went. A ball under
 * a card held is not drawn either: half past the edge of the card it is on, it
 * showed beside the card held over that one. A line draws itself in, from the
 * one it leaves to the one it comes to, and back to the one it leaves from as
 * it is let go - the end it comes to gone first, the one it leaves from
 * after; its card taken off the list (a payment booked), it swooshes on into
 * the end it comes to instead - taken up again on its way out, it is all
 * there again - and one whose ends go to other
 * cards - a suggestion picked in place of another, a payment opened in place
 * of another - goes over to them from where it was, rather than jumping
 * there. The ball it comes to shows once it is there (``data-link-arriving``
 * till then).
 */
export function TriageLinks() {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    let frame = 0;
    type Point = { x: number; y: number };
    type Line = {
      path: SVGPathElement;
      from: HTMLElement;
      to: HTMLElement;
      drawn: { start: Point; end: Point };
      // Going over to other cards: drawn from where it was (``was``) to where
      // its ends are, a share of the way (``blend``) as it goes.
      was: { start: Point; end: Point } | null;
      blend: number;
      move: AnimationPlaybackControls | null;
      // Drawing itself in, or back out as it is let go (``isLeaving``).
      dash: AnimationPlaybackControls | null;
      isLeaving: boolean;
      // The row of the card it leaves from: taken off its list, the line
      // swooshes away.
      row: HTMLElement | null;
      // The ball it is on its way to, out of sight till it gets there.
      arriving: HTMLElement | null;
    };
    // A line each end it leaves from - by its name, and which of that name's
    // ends it is, so a line whose ends go to other cards is the same line,
    // going over to them.
    const lines = new Map<string, Line>();
    const isStill = () =>
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // The ball a line is on its way to, out of sight till it is there - the
    // one it was on its way to before shown again.
    const arriveAt = (line: Line, ball: HTMLElement | null) => {
      if (line.arriving && line.arriving !== ball) {
        delete line.arriving.dataset.linkArriving;
      }
      line.arriving = ball;
      if (ball) ball.dataset.linkArriving = "";
    };
    const arrive = (line: Line, ball: HTMLElement) => {
      if (line.arriving !== ball) return;
      delete ball.dataset.linkArriving;
      line.arriving = null;
    };
    // How much of it is drawn, run from one share to another: 1 none of it,
    // 0 all of it.
    const runDash = (
      line: Line,
      from: number,
      to: number,
      ease: "easeIn" | "easeOut",
      seconds: number,
      onDone: () => void,
    ) => {
      line.dash?.stop();
      line.path.setAttribute("pathLength", "1");
      line.path.style.strokeDasharray = "1";
      line.path.style.strokeDashoffset = String(from);
      line.dash = animate(from, to, {
        duration: seconds,
        ease,
        onUpdate: (left) => {
          line.path.style.strokeDashoffset = String(left);
        },
        onComplete: () => {
          line.dash = null;
          onDone();
        },
      });
    };
    const drawWhole = (line: Line) => {
      line.dash?.stop();
      line.dash = null;
      line.path.removeAttribute("pathLength");
      line.path.style.strokeDasharray = "";
      line.path.style.strokeDashoffset = "";
      delete line.from.dataset.linkLeaving;
    };
    // Its card's row taken off its list - its payment booked, moved, held.
    const isTakenOff = (row: HTMLElement | null) =>
      row != null &&
      (!row.isConnected || row.closest("[data-motion-pop-id]") != null);
    // Let go: back to the end it leaves from - that end held in sight till
    // it is there, the one it came to gone already - or, its card taken off
    // its list, swooshed on into the end it came to, quickly; taken off once
    // it has gone.
    const letGo = (key: string, line: Line) => {
      line.move?.stop();
      line.move = null;
      arriveAt(line, null);
      const goes = () => {
        line.path.remove();
        if (lines.get(key) === line) lines.delete(key);
      };
      if (isStill()) {
        line.dash?.stop();
        goes();
        return;
      }
      line.isLeaving = true;
      const isSwooshed = isTakenOff(line.row);
      const leavesFrom = line.from;
      // Held in sight till the line is back - but for one fading out with
      // its list (Similar's, as it closes), which goes with it.
      if (!isSwooshed && leavesFrom.closest("[data-balls-hidden]") == null) {
        leavesFrom.dataset.linkLeaving = "";
      }
      runDash(
        line,
        Number(line.path.style.strokeDashoffset || 0),
        isSwooshed ? -1 : 1,
        "easeIn",
        isSwooshed ? TRIAGE_LINK_SWOOSH_SECONDS : TRIAGE_LINK_MOVE_SECONDS,
        () => {
          delete leavesFrom.dataset.linkLeaving;
          goes();
        },
      );
    };
    const findMiddle = (element: HTMLElement) => {
      const box = element.getBoundingClientRect();
      return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    };
    // The list it is in: what a line to it is drawn inside.
    const findList = (element: HTMLElement) =>
      element.closest<HTMLElement>(
        "[data-selectable-card-list], [data-link-clip]",
      );
    // Under what is held over its list's rows as they scroll past
    // (``data-link-cover``) - the row the keys are on, held at its top or
    // foot - and so not drawn, nor any line from it. Level, not inside: a
    // ball sits half past its card's edge, beside the card held, and is under
    // it all the same.
    // Each list's held rows, read once a frame - not once a ball, which
    // made a frame long enough to catch rows half way through a move.
    let heldIn = new Map<
      HTMLElement,
      { cover: HTMLElement; top: number; bottom: number }[]
    >();
    const isCovered = (element: HTMLElement, at: { y: number }) => {
      const list = findList(element);
      if (!list) return false;
      let held = heldIn.get(list);
      if (!held) {
        held = [
          ...list.querySelectorAll<HTMLElement>(
            "[data-selectable-row-active], [data-link-cover]",
          ),
        ].map((cover) => {
          const box = cover.getBoundingClientRect();
          return { cover, top: box.top, bottom: box.bottom };
        });
        heldIn.set(list, held);
      }
      return held.some(
        ({ cover, top, bottom }) =>
          !cover.contains(element) && at.y >= top && at.y <= bottom,
      );
    };
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const svg = svgRef.current;
      if (!svg) return;
      heldIn = new Map();
      // Each ball's place read first, every one, then each shown or hidden:
      // a write between two reads lays the page out again for the second.
      const balls = [
        ...document.querySelectorAll<HTMLElement>(
          "[data-link-from], [data-link-to]",
        ),
      ];
      const hidden = balls.map((ball) => isCovered(ball, findMiddle(ball)));
      balls.forEach((ball, at) => {
        if ((ball.style.visibility === "hidden") !== hidden[at]) {
          ball.style.visibility = hidden[at] ? "hidden" : "";
        }
      });
      const kept = new Set<string>();
      const ordinals = new Map<string, number>();
      for (const from of document.querySelectorAll<HTMLElement>(
        "[data-link-from][data-link-engaged='true']",
      )) {
        const link = from.dataset.linkFrom ?? "";
        const ordinal = ordinals.get(link) ?? 0;
        ordinals.set(link, ordinal + 1);
        const key = `${link}#${ordinal}`;
        const to = document.querySelector<HTMLElement>(
          `[data-link-to="${CSS.escape(link)}"][data-link-engaged='true']`,
        );
        if (!to) continue;
        // Nothing to or from a ball fading out with its list (Similar's, as
        // it closes), nor one no longer laid out - its box is the page's
        // corner, and a line to it shot off there.
        if (
          [from, to].some(
            (ball) =>
              ball.closest("[data-balls-hidden]") != null ||
              ball.getClientRects().length === 0,
          )
        ) {
          continue;
        }
        const start = findMiddle(from);
        const end = findMiddle(to);
        if (isCovered(from, start) || isCovered(to, end)) continue;
        // Leaving a card scrolled out of its list - still lined up, a row of
        // Similar's past the edge - it comes in from the list's edge it went
        // past: the line runs on off it, where the card is.
        const fromList = findList(from)?.getBoundingClientRect();
        const fromClipped =
          fromList && (start.y < fromList.top || start.y > fromList.bottom)
            ? start.y < fromList.top
              ? fromList.top
              : fromList.bottom
            : null;
        const origin = { x: start.x, y: fromClipped ?? start.y };
        // Coming to one scrolled out of its list - the suggestion picked - it
        // runs on up or down to the list's edge, where it went, and stops
        // there: clipped, nothing along the edge.
        const toList = findList(to)?.getBoundingClientRect();
        const clipped =
          toList && (end.y < toList.top || end.y > toList.bottom)
            ? end.y < toList.top
              ? toList.top
              : toList.bottom
            : null;
        const target = { x: end.x, y: clipped ?? end.y };
        let line = lines.get(key);
        if (line?.isLeaving) {
          // Taken up again on its way out: whole again, and over to where
          // its ends are now.
          line.isLeaving = false;
          drawWhole(line);
        }
        if (
          line &&
          (line.from !== from || line.to !== to) &&
          isTakenOff(line.row)
        ) {
          // Its card taken off its list - its payment booked - and the line
          // on to the next one's: this one swooshed away, a new one drawn in.
          const swooshed = `${key}~${performance.now()}`;
          lines.delete(key);
          lines.set(swooshed, line);
          letGo(swooshed, line);
          line = undefined;
        }
        if (!line) {
          const path = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "path",
          );
          path.setAttribute("fill", "none");
          path.setAttribute("stroke", "var(--primary)");
          path.setAttribute("stroke-width", "1.5");
          path.dataset.testid = "triage-link-curve";
          path.dataset.link = link;
          svg.appendChild(path);
          const drawing: Line = {
            path,
            from,
            to,
            drawn: { start: origin, end: target },
            was: null,
            blend: 1,
            move: null,
            dash: null,
            isLeaving: false,
            row: null,
            arriving: null,
          };
          line = drawing;
          lines.set(key, drawing);
          if (!isStill()) {
            // Drawn in, from the end it leaves to the one it comes to - that
            // one shown once it is there.
            arriveAt(drawing, to);
            runDash(drawing, 1, 0, "easeOut", TRIAGE_LINK_MOVE_SECONDS, () => {
              drawWhole(drawing);
              arrive(drawing, to);
            });
          }
        } else if (line.from !== from || line.to !== to) {
          // Its ends on other cards: over to them from where it was drawn -
          // and, coming to another ball, that ball out of sight till it is
          // there.
          const going = line;
          const toBall = going.to !== to ? to : null;
          going.from = from;
          going.to = to;
          going.move?.stop();
          if (isStill()) {
            going.was = null;
            going.blend = 1;
            going.move = null;
          } else {
            going.was = { start: going.drawn.start, end: going.drawn.end };
            going.blend = 0;
            if (toBall) arriveAt(going, toBall);
            going.move = animate(0, 1, {
              duration: TRIAGE_LINK_MOVE_SECONDS,
              ease: "easeInOut",
              onUpdate: (share) => {
                going.blend = share;
              },
              onComplete: () => {
                going.was = null;
                going.blend = 1;
                going.move = null;
                if (toBall) arrive(going, toBall);
              },
            });
          }
        }
        line.row = from.closest<HTMLElement>("[data-selectable-row]");
        const share = line.blend;
        const between = (was: Point, now: Point) => ({
          x: was.x + (now.x - was.x) * share,
          y: was.y + (now.y - was.y) * share,
        });
        const drawnStart = line.was ? between(line.was.start, origin) : origin;
        const drawnEnd = line.was ? between(line.was.end, target) : target;
        line.drawn = { start: drawnStart, end: drawnEnd };
        // In steps: level out of the one, across to half way, up or down to
        // the other's height there, and level into it - each turn rounded,
        // as tight as the room either side of it allows. An end past its
        // list's edge has no turn: the line runs on off the edge there.
        const across = drawnStart.x + (drawnEnd.x - drawnStart.x) / 2;
        const out = Math.sign(across - drawnStart.x);
        const down = Math.sign(drawnEnd.y - drawnStart.y);
        const into = Math.sign(drawnEnd.x - across);
        const turns = (fromClipped != null ? 0 : 1) + (clipped != null ? 0 : 1);
        const radius =
          turns === 0
            ? 0
            : Math.min(
                8,
                Math.abs(across - drawnStart.x),
                Math.abs(drawnEnd.y - drawnStart.y) / turns,
              );
        const leaves =
          fromClipped != null
            ? `M ${across} ${drawnStart.y}`
            : `M ${drawnStart.x} ${drawnStart.y} H ${across - out * radius} Q ${across} ${drawnStart.y} ${across} ${drawnStart.y + down * radius}`;
        const comes =
          clipped != null
            ? `V ${drawnEnd.y}`
            : `V ${drawnEnd.y - down * radius} Q ${across} ${drawnEnd.y} ${across + into * radius} ${drawnEnd.y} H ${drawnEnd.x}`;
        line.path.setAttribute("d", `${leaves} ${comes}`);
        kept.add(key);
      }
      for (const [key, line] of lines) {
        if (!kept.has(key) && !line.isLeaving) letGo(key, line);
      }
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      for (const line of lines.values()) {
        line.move?.stop();
        line.dash?.stop();
        arriveAt(line, null);
        delete line.from.dataset.linkLeaving;
        line.path.remove();
      }
    };
  }, []);

  // On the page itself, over both panels and the divider between them - not
  // under the cards of either - and under any popup or dialog.
  return createPortal(
    <svg
      ref={svgRef}
      aria-hidden
      data-testid="triage-links"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "visible",
        zIndex: 999,
      }}
    />,
    document.body,
  );
}
